import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, RefreshCw, ArrowRight, ShieldCheck, Lock } from 'lucide-react';

const STATE = {
  GREEN: { dot: 'bg-emerald-500', ring: 'border-emerald-200 bg-emerald-50/40', text: 'text-emerald-700', label: 'Ready' },
  AMBER: { dot: 'bg-amber-500', ring: 'border-amber-200 bg-amber-50/40', text: 'text-amber-700', label: 'Attention' },
  RED: { dot: 'bg-rose-500', ring: 'border-rose-200 bg-rose-50/40', text: 'text-rose-700', label: 'Action needed' },
};
// Severity vocabulary (RD-02): BLOCK = a backend workflow refuses; REQUIRED = setup item still to complete (nothing stops you yet); WARNING = advisory.
const SEVERITY = {
  BLOCK: { label: 'BLOCKS', cls: 'border-rose-300 text-rose-700 bg-rose-50', title: 'A backend workflow refuses until this is resolved' },
  REQUIRED: { label: 'REQUIRED', cls: 'border-orange-300 text-orange-700 bg-orange-50', title: 'Required setup item — not yet enforced by the system' },
  WARN: { label: 'WARNING', cls: 'border-amber-300 text-amber-700', title: 'Advisory — service delivery is not prevented' },
};

function AreaCard({ area, onAction }) {
  const s = STATE[area.state] || STATE.GREEN;
  return (
    <div className={`rounded-lg border p-4 space-y-3 ${s.ring}`} data-testid={`readiness-area-${area.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.dot}`} />
          <h3 className="text-sm font-semibold">{area.label}</h3>
        </div>
        <span className={`text-[11px] font-semibold uppercase tracking-wider ${s.text}`} data-testid={`readiness-state-${area.key}`}>{area.state}</span>
      </div>
      {area.findings.length === 0 ? (
        <p className="text-xs text-muted-foreground">No issues from {area.source}.</p>
      ) : (
        <ul className="space-y-1">
          {area.findings.map((f, i) => (
            <li key={i} className="text-xs flex items-start gap-1.5" data-testid={`readiness-finding-${area.key}-${f.code}`}>
              <Badge variant="outline" className={`text-[10px] px-1 py-0 whitespace-nowrap ${SEVERITY[f.severity]?.cls || SEVERITY.WARN.cls}`} title={SEVERITY[f.severity]?.title} data-testid={`readiness-severity-${area.key}-${f.code}`}>{f.severity === 'BLOCK' ? `BLOCKS ${f.blocks || 'workflow'}` : (SEVERITY[f.severity]?.label || f.severity)}</Badge>
              <span>{f.message}</span>
            </li>
          ))}
        </ul>
      )}
      {area.actions.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {area.actions.map(a => (
            <Button key={a.key} size="sm" variant={a.always ? 'ghost' : 'outline'} className="h-7 text-xs" disabled={!a.allowed} title={a.allowed ? a.route : `Requires ${a.permission}`} onClick={() => onAction(a)} data-testid={`readiness-action-${area.key}-${a.key}`}>
              {!a.allowed && <Lock className="w-3 h-3 mr-1" />}{a.label}<ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

// Day 14 — Participant Readiness Centre: composition over existing rules (no business logic here).
export default function ParticipantReadinessCentrePage() {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/participants/${participantId}/readiness-centre`); setData(r.data?.data || null); }
    catch (e) { notify.error(e, 'READINESS_LOAD_FAILED'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [participantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const overall = data ? STATE[data.overall] : null;
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="participant-readiness-centre">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2 h-7 text-xs" onClick={() => navigate(`/participants/${participantId}`)} data-testid="readiness-back-btn"><ArrowLeft className="w-3.5 h-3.5 mr-1" />Participant</Button>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="w-6 h-6" />Participant Readiness Centre</h1>
          <p className="text-sm text-muted-foreground">Profile → Documents → Agreement → Funding → Schedule → Roster → Delivery → Evidence → Billing — the single participant-readiness view. States come from the existing engines; every finding names its source.</p>
          <p className="text-[11px] text-muted-foreground" data-testid="readiness-legend"><span className="font-semibold text-rose-700">BLOCKS …</span> a workflow the system refuses · <span className="font-semibold text-orange-700">REQUIRED</span> setup still to complete (not enforced) · <span className="font-semibold text-amber-700">WARNING</span> advisory only</p>
        </div>
        <div className="flex items-center gap-2">
          {overall && <span className={`text-xs font-bold px-3 py-1 rounded-full border ${overall.ring} ${overall.text}`} data-testid="readiness-overall">{data.overall} · {data.counts.RED} red · {data.counts.AMBER} amber · {data.counts.GREEN} green</span>}
          <Button variant="outline" size="sm" className="h-8" onClick={load} disabled={loading} data-testid="readiness-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>
      {loading && !data && <p className="text-sm text-muted-foreground" data-testid="readiness-loading">Evaluating readiness…</p>}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.areas.map(a => <AreaCard key={a.key} area={a} onAction={(act) => navigate(act.route)} />)}
        </div>
      )}
      {data && <p className="text-[11px] text-muted-foreground">As at {data.as_at}. Sources: {data.sources.join(', ')}.</p>}
    </div>
  );
}
